import type { SessionResponse } from '@lk/shared';
import { getDb, type LearnKidsDb, type StoredSession } from './db';

/**
 * Сесія зберігається локально ЦІЛКОМ (усі питання приходять із сервера на старті).
 * Завдяки цьому:
 *   · F5 посеред сесії не втрачає ані питань, ані місця, де дитина зупинилася;
 *   · зникнення інтернету не зупиняє гру — дитина спокійно дограє офлайн.
 */
export async function saveSession(
  session: SessionResponse,
  learnerId: string,
  db: LearnKidsDb = getDb(),
): Promise<StoredSession> {
  const existing = await db.sessions.get(session.sessionId);
  const row: StoredSession = {
    sessionId: session.sessionId,
    learnerId,
    payload: session,
    cursor: existing?.cursor ?? 0,
    startedAt: existing?.startedAt ?? Date.now(),
    finishedAt: existing?.finishedAt ?? null,
  };
  await db.sessions.put(row);
  return row;
}

export function loadSession(
  sessionId: string,
  db: LearnKidsDb = getDb(),
): Promise<StoredSession | undefined> {
  return db.sessions.get(sessionId);
}

export async function saveCursor(
  sessionId: string,
  cursor: number,
  db: LearnKidsDb = getDb(),
): Promise<void> {
  const row = await db.sessions.get(sessionId);
  if (!row) return;
  // Курсор тільки росте — щоб випадковий рендер не відкотив дитину назад.
  if (cursor <= row.cursor) return;
  await db.sessions.put({ ...row, cursor });
}

export async function finishSession(
  sessionId: string,
  db: LearnKidsDb = getDb(),
): Promise<void> {
  const row = await db.sessions.get(sessionId);
  if (!row || row.finishedAt) return;
  await db.sessions.put({ ...row, finishedAt: Date.now() });
}

/** Незавершена сесія цієї дитини — щоб запропонувати «продовжити». */
export async function findUnfinishedSession(
  learnerId: string,
  db: LearnKidsDb = getDb(),
): Promise<StoredSession | undefined> {
  const rows = await db.sessions.where('learnerId').equals(learnerId).toArray();
  return rows
    .filter((row) => row.finishedAt === null && row.cursor < row.payload.items.length)
    .sort((a, b) => b.startedAt - a.startedAt)[0];
}

/** Старі завершені сесії локально не потрібні — історія живе на сервері. */
export async function pruneSessions(
  olderThanMs = 3 * 24 * 3600_000,
  db: LearnKidsDb = getDb(),
): Promise<void> {
  const cutoff = Date.now() - olderThanMs;
  const rows = await db.sessions.toArray();
  const ids = rows
    .filter((row) => row.finishedAt !== null && row.finishedAt < cutoff)
    .map((row) => row.sessionId);
  if (ids.length) await db.sessions.bulkDelete(ids);
}
